import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import { cva } from "class-variance-authority";

import { cn } from "../../utils";

const badgeStatusVariants = cva(
  "af-inline-flex af-items-center af-gap-1 af-rounded-md af-bg-accent af-px-2.5 af-py-1 af-text-xs af-font-semibold af-text-secondary-foreground [&>svg:not([class*=size-]):not([class*=-h-]):not([class*=-w-])]:af-size-3",
  {
    variants: {
      variant: {
        default: "[&_[data-indicator]]:af-bg-muted-foreground/60",
        active: "[&_[data-indicator]]:af-bg-primary/70",
        attention:
          "[&_[data-indicator]]:af-size-1 [&_[data-indicator]]:af-bg-primary [&_[data-ping-indicator]]:af-block [&_[data-ping-indicator]]:af-bg-primary",
        success: "[&_[data-indicator]]:af-bg-success-foreground",
        informational: "",
        inactive: "",
        destructive: "",
      },
      colored: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "attention",
        colored: true,
        className:
          "af-border af-border-attention-foreground/30 af-bg-attention af-text-attention-foreground",
      },
      {
        variant: "active",
        colored: true,
        className:
          "af-border af-border-active-foreground/30 af-bg-active af-text-active-foreground",
      },
      {
        variant: "success",
        colored: true,
        className:
          "af-border af-border-success-foreground/30 af-bg-success af-text-success-foreground",
      },
      {
        variant: "destructive",
        colored: true,
        className:
          "af-border af-border-destructive/30 af-bg-destructive-foreground af-text-destructive",
      },
      {
        variant: "informational",
        colored: true,
        className:
          "af-border af-border-informational-foreground/30 af-bg-informational af-text-informational-foreground",
      },
      {
        variant: "inactive",
        colored: true,
        className:
          "af-border af-border-inactive-foreground/30 af-bg-inactive af-text-inactive-foreground",
      },
    ],
    defaultVariants: {
      variant: "default",
      colored: false,
    },
  },
);

export type BadgeStatusProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof badgeStatusVariants>;

function BadgeStatus({
  className,
  variant,
  colored,
  children,
  ...props
}: BadgeStatusProps) {
  return (
    <span
      className={cn(badgeStatusVariants({ variant, colored }), className)}
      {...props}
    >
      {!colored && (
        <span className="af-relative af-flex af-size-3 af-items-center af-justify-center">
          <Indicator
            data-ping-indicator
            className="af-absolute af-hidden af-animate-ping"
          />
          <Indicator data-indicator className="af-relative" />
        </span>
      )}
      {children}
    </span>
  );
}

function Indicator({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      role="presentation"
      className={cn("af-size-1.5 af-rounded-full", className)}
      {...props}
    />
  );
}

export { BadgeStatus, badgeStatusVariants };
